import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './AuditLog.css';
import API_URL from '../config';

const AuditLog = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [outcomeFilter, setOutcomeFilter] = useState('ALL');
  const [ipFilter, setIpFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/audit/logs?limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data);
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [token, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Apply filters
  useEffect(() => {
    let result = [...logs].sort((a, b) => 
      new Date(b.published) - new Date(a.published)
    );

    if (severityFilter !== 'ALL') {
      result = result.filter(log => log.severity === severityFilter);
    }
    if (outcomeFilter !== 'ALL') {
      result = result.filter(log => log.outcome === outcomeFilter);
    }
    if (ipFilter.trim()) {
      result = result.filter(log => log.ipAddress.includes(ipFilter.trim()));
    }

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(log =>
        log.actor?.toLowerCase().includes(term) ||
        log.displayMessage?.toLowerCase().includes(term) ||
        log.eventType?.toLowerCase().includes(term) ||
        log.target?.toLowerCase().includes(term)
      );
    }

    setFiltered(result);
  }, [logs, severityFilter, outcomeFilter, ipFilter, search]);

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const handleExportCSV = () => {
    const headers = ['Time', 'Event Type', 'Message', 'Actor', 'Target', 'Severity', 'Outcome', 'IP Address'];
    const rows = filtered.map(log => [
      formatDate(log.published),
      log.eventType,
      log.displayMessage,
      log.actor,
      log.target,
      log.severity,
      log.outcome,
      log.ipAddress
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `okta-audit-log-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#003366' }}>Audit Log</h1>
        <p style={{ fontSize: '14px', color: '#666666', marginTop: '4px' }}>
          Okta System Log — real-time identity events for ABC Corp
        </p>
      </div>

      <div className="audit-toolbar">
        <input
          type="text"
          placeholder="Search by actor, event, or target..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
          <option value="ALL">All Severities</option>
          <option value="INFO">INFO</option>
          <option value="WARN">WARN</option>
          <option value="ERROR">ERROR</option>
          <option value="DEBUG">DEBUG</option>
        </select>
        <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)}>
          <option value="ALL">All Outcomes</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAILURE">FAILURE</option>
          <option value="SKIPPED">SKIPPED</option>
          <option value="CHALLENGE">CHALLENGE</option>
          <option value="ALLOW">ALLOW</option>
          <option value="DENY">DENY</option>
          <option value="UNKNOWN">UNKNOWN</option>
        </select>
        <input
          type="text"
          placeholder="Filter by IP address..."
          value={ipFilter}
          onChange={e => setIpFilter(e.target.value)}
          style={{ maxWidth: '180px' }}
        />
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}>
          <option value={50}>Last 50 events</option>
          <option value={100}>Last 100 events</option>
          <option value={200}>Last 200 events</option>
        </select>
        <button className="btn-refresh" onClick={fetchLogs}>Refresh</button>
        <button className="btn-export" onClick={handleExportCSV}>Export CSV</button>
      </div>

      <div className="audit-meta">
        <span>Showing <span className="audit-count">{filtered.length}</span> of {logs.length} events</span>
        <span>Last updated: {new Date().toLocaleTimeString()}</span>
      </div>

      <div className="audit-table-container">
        {loading ? (
          <p style={{ padding: '20px', fontSize: '14px', color: '#999' }}>Loading audit logs...</p>
        ) : filtered.length === 0 ? (
          <p style={{ padding: '20px', fontSize: '14px', color: '#999' }}>No events found.</p>
        ) : (
          <table className="audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Event Type</th>
                <th>Message</th>
                <th>Actor</th>
                <th>Target</th>
                <th>Severity</th>
                <th>Outcome</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log, index) => (
                <tr key={log.uuid || index}>
                  <td>{formatDate(log.published)}</td>
                  <td>{log.eventType}</td>
                  <td>{log.displayMessage}</td>
                  <td>{log.actor}</td>
                  <td>{log.target}</td>
                  <td>
                    <span className={`severity-badge severity-${log.severity}`}>
                      {log.severity}
                    </span>
                  </td>
                  <td>
                    <span className={`outcome-${log.outcome}`}>
                      {log.outcome}
                    </span>
                  </td>
                  <td>{log.ipAddress}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
};

export default AuditLog;